// The ask in one plain line, for the card at the top of Today:
// "Any sport · Any risk · Any bookmaker · Today".
//
// Pure: the selections, the preset (if one is chosen) and the book's display
// name in, the line out. Each part starts with a capital, because each is read
// on its own; nothing here is ever an em dash (src/lib/copy-rules.test.js).

import { sportLabel } from '../engine/teams.js';
import { legsWord, paysWord } from './list-tools.js';

const WHEN_WORD = { today: 'Today', tonight: 'Tonight', tomorrow: 'Tomorrow', now: 'Next 3 hrs' };
const RISK_WORD = { low: 'Low risk', medium: 'Medium risk', high: 'High risk', outrageous: 'Outrageous' };

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const list = (names) => (names.length <= 2 ? names.join(' and ') : `${names.slice(0, -1).join(', ')} and ${names.at(-1)}`);

/** Sports, said in the fewest words that are still true. */
function sportsPart(chips) {
  const by = { include: [], only: [], prefer: [], avoid: [] };
  let games = 0;
  for (const [key, state] of Object.entries(chips ?? {})) {
    if (!state) continue;
    if (key.startsWith('sport:') && by[state]) by[state].push(sportLabel(key.slice(6)));
    else if (key.startsWith('event:') && state !== 'avoid') games += 1;
  }
  const parts = [];
  if (by.only.length) parts.push(`Only ${list(by.only)}`);
  const wanted = [...by.include, ...by.prefer];
  if (wanted.length) parts.push(list(wanted));
  if (games) parts.push(`${games} game${games === 1 ? '' : 's'}`);
  if (by.avoid.length) parts.push(`${parts.length ? 'no' : 'Any sport but'} ${list(by.avoid)}`);
  return parts.length ? cap(parts.join(', ')) : 'Any sport';
}

/**
 * selections: askSelections. preset: the chosen preset or null. bookName: what
 * the Bookmaker row calls the book the answer is written at, or null for any.
 */
export function askSummary(selections, { preset = null, bookName = null } = {}) {
  const s = selections ?? {};
  const parts = [];
  if (preset) parts.push(preset.label);
  parts.push(sportsPart(s.chips));
  const locked = s.include?.length ?? 0;
  if (locked) parts.push(`${locked} leg${locked === 1 ? '' : 's'} locked`);
  // A price range stands in for the risk band: the engine takes one or the other.
  if (s.odds) parts.push(`Pays ${paysWord(s.odds)}`);
  else if (!preset?.requests?.some((r) => r.risk)) parts.push(RISK_WORD[s.risk] ?? 'Any risk');
  if (s.legs) parts.push(`${legsWord(s.legs)} leg${s.legs === 1 ? '' : 's'}`);
  parts.push(bookName ?? 'Any bookmaker');
  parts.push(WHEN_WORD[s.when] ?? 'Today');
  return parts.join(' · ');
}

/** How many multis fit, the way the list header says it: "3,800+", "42". */
export function matchCount(n) {
  if (!(n > 0)) return '0';
  return n >= 1000 ? `${(Math.floor(n / 100) * 100).toLocaleString('en-AU')}+` : n.toLocaleString('en-AU');
}
